import React from "react";
import SectionTitle from "../../components/layouts/others/title-section.layout";
import { Box, Chip, Container, Divider, Paper, Stack, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBullhorn,
    faCalendarDays,
    faCheckCircle,
    faClock,
    faLocationDot,
    faVideo,
} from "@fortawesome/free-solid-svg-icons";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    LabelList,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

const firstRoundResults = [
    {
        candidate: "Jean-Philippe GUIBERT",
        candidateShort: "J.-P. GUIBERT",
        status: "Qualifiée",
        nuance: "Divers",
        list: "VLD - VIVRE LA DEMOCRATIE",
        percentValue: 32.82,
        votesValue: 1103,
        color: "#1565c0",
    },
    {
        candidate: "Patrick GAUTHIER",
        candidateShort: "P. GAUTHIER",
        status: "Qualifiée",
        nuance: "Divers écologiste",
        list: "RÉUSSIR ENSEMBLE ÉCOLOGIE ÉCONOMIE SOCIAL",
        percentValue: 30.76,
        votesValue: 1034,
        color: "#2e7d32",
    },
    {
        candidate: "Pierre-Antoine VERSINI",
        candidateShort: "P.-A. VERSINI",
        status: "Qualifiée",
        nuance: "Divers gauche",
        list: "LISTE ECO-CITOYENNE",
        percentValue: 23.15,
        votesValue: 778,
        color: "#ef6c00",
    },
    {
        candidate: "David DINTILHAC",
        candidateShort: "D. DINTILHAC",
        status: "Qualifiée",
        nuance: "Divers centre",
        list: "UNIS POUR BOIS-LE-ROI",
        percentValue: 13.27,
        votesValue: 446,
        color: "#6d4c41",
    },
];

const POST20260316: React.FC = () => {
    const formatPercent = (value: number) => `${value.toFixed(2).replace(".", ",")} %`;
    const formatVotes = (value: number) => `${new Intl.NumberFormat("fr-FR").format(value)} voix`;

    return (
        <Container maxWidth="lg" sx={{ mb: 8 }}>
            <SectionTitle title="ESPACE CAMPAGNE VLD 2026" />

            <Box mt={3} mb={5}>
                <Typography variant="h6" fontWeight={700} align="center" gutterBottom color="primary">
                    <FontAwesomeIcon icon={faBullhorn} style={{ marginRight: 8 }} />
                    VLD2026 - Résultats du 1er tour (16&nbsp;mars&nbsp;2026)
                </Typography>
                <Divider sx={{ mt: 1, mb: 3 }} />
            </Box>

            <Stack spacing={4}>
                <Paper
                    elevation={4}
                    sx={{
                        p: { xs: 3, md: 4 },
                        borderRadius: 4,
                        border: "1px solid rgba(25,118,210,0.14)",
                        background:
                            "linear-gradient(135deg, rgba(13,71,161,0.06) 0%, rgba(25,118,210,0.03) 55%, rgba(67,160,71,0.05) 100%)",
                    }}
                >
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                        <Chip icon={<FontAwesomeIcon icon={faCheckCircle} />} label="Municipales 2026" sx={{ fontWeight: 700 }} />
                    </Stack>

                    <Typography variant="h4" fontWeight={900} color="#0d47a1" sx={{ mb: 1.5 }}>
                        Premier tour : VLD en tête à Bois-le-Roi
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                        Avec <b>1&nbsp;103 voix</b> soit <b>32,82%</b> des suffrages exprimés, la liste <b>VLD - Vivre la Démocratie</b> se place en tête
                        du premier tour du 16 mars 2026.
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mt: 1, lineHeight: 1.75 }}>
                        Cette dynamique renforce notre responsabilité et notre détermination à mobiliser largement pour le second tour du
                        <b> dimanche 22 mars 2026</b>.
                    </Typography>
                </Paper>

                <Paper elevation={3} sx={{ p: { xs: 3, md: 4 }, borderRadius: 4, borderLeft: "6px solid #1565c0" }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
                        <FontAwesomeIcon icon={faCheckCircle} style={{ color: "#1565c0" }} />
                        <Typography variant="h5" fontWeight={900} color="#1565c0">
                            Résultats du 1er tour
                        </Typography>
                    </Stack>

                    <Box sx={{ height: 360, mt: 1.2, bgcolor: "#f8fbff", borderRadius: 3, p: 2, border: "1px solid rgba(21,101,192,0.14)" }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={firstRoundResults}
                                layout="vertical"
                                margin={{ top: 8, right: 36, left: 8, bottom: 8 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                                <XAxis type="number" domain={[0, 35]} tickFormatter={(value: number) => `${value}%`} />
                                <YAxis type="category" dataKey="candidateShort" width={120} />
                                <Tooltip
                                    formatter={(value: any, name: string, item: any) => {
                                        if (name === "Pourcentage") {
                                            return [formatPercent(Number(value)), name];
                                        }
                                        return [formatVotes(item.payload.votesValue), "Voix"];
                                    }}
                                    labelFormatter={(_, payload: any) => payload?.[0]?.payload?.candidate || ""}
                                />
                                <Bar dataKey="percentValue" name="Pourcentage" radius={[0, 10, 10, 0]}>
                                    <LabelList
                                        dataKey="percentValue"
                                        position="right"
                                        formatter={(value: any) => formatPercent(Number(value))}
                                        style={{ fontWeight: 800, fill: "#1f2937" }}
                                    />
                                    {firstRoundResults.map((entry) => (
                                        <Cell key={entry.candidate} fill={entry.color} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>

                    <Grid container spacing={1.5} sx={{ mt: 1.5 }}>
                        {firstRoundResults.map((result) => (
                            <Grid size={{ xs: 12, md: 6 }} key={result.candidate}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 2.5,
                                        border: `1px solid ${result.color}2A`,
                                        bgcolor: `${result.color}08`,
                                    }}
                                >
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography variant="body2" fontWeight={900} sx={{ color: result.color }}>
                                            {result.candidate}
                                        </Typography>
                                        <Typography variant="body2" fontWeight={800} color="text.secondary">
                                            {formatVotes(result.votesValue)}
                                        </Typography>
                                    </Stack>
                                    <Typography variant="caption" color="text.secondary">
                                        {result.status} - {result.nuance}
                                    </Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Paper>

                <Paper elevation={3} sx={{ p: { xs: 3, md: 4 }, borderRadius: 4 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
                        <FontAwesomeIcon icon={faVideo} style={{ color: "#ef6c00" }} />
                        <Typography variant="h5" fontWeight={900} color="#ef6c00">
                            Message vidéo de Jean-Philippe Guibert
                        </Typography>
                    </Stack>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.75 }}>
                        Jean-Philippe remercie les Bacots pour leur confiance et appelle à une nouvelle mobilisation pour le 2nd tour.
                        Il ne faut rien lâcher.
                    </Typography>

                    <Box
                        sx={{
                            borderRadius: 3,
                            overflow: "hidden",
                            position: "relative",
                            pt: "56.25%",
                            bgcolor: "black",
                            boxShadow: "0 12px 28px rgba(0,0,0,0.18)",
                        }}
                    >
                        <iframe
                            style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: 0 }}
                            src="https://www.youtube.com/embed/QW_CPiZFN9Y"
                            title="Message de remerciement - 1er tour municipales 2026"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    </Box>
                </Paper>

                <Paper
                    elevation={3}
                    sx={{
                        p: { xs: 3, md: 4 },
                        borderRadius: 4,
                        border: "1px solid rgba(239,108,0,0.25)",
                        bgcolor: "rgba(239,108,0,0.06)",
                    }}
                >
                    <Typography variant="h5" fontWeight={900} color="#d84315" sx={{ mb: 1 }}>
                        Prochain rendez-vous de mobilisation
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2.2, lineHeight: 1.75 }}>
                        Nous vous donnons rendez-vous à la 4ème réunion d&apos;information avant le 2nd tour.
                    </Typography>

                    <Stack spacing={1.1}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <FontAwesomeIcon icon={faCalendarDays} style={{ color: "#1565c0" }} />
                            <Typography variant="body1" fontWeight={800} color="#37474f">
                                Jeudi 19 mars 2026
                            </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <FontAwesomeIcon icon={faClock} style={{ color: "#1565c0" }} />
                            <Typography variant="body1" fontWeight={800} color="#37474f">
                                20h
                            </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <FontAwesomeIcon icon={faLocationDot} style={{ color: "#1565c0" }} />
                            <Typography variant="body1" fontWeight={800} color="#37474f">
                                Préau de l&apos;école Olivier Métra
                            </Typography>
                        </Stack>
                    </Stack>
                </Paper>
            </Stack>
        </Container>
    );
};

export default POST20260316;
